import { useEffect } from 'react'
import { UseQueryResult } from '@tanstack/react-query'
import { WorkerCounter } from '../../api/worker-counters'
import {
  useWorkerCountersQuery,
  useCreateOrGetCounterMutation,
} from '../../queries/worker-counters'
import { useWorkerCounterTemplatesQuery } from '../../queries/worker-counter-templates'
import { compareDocsById } from '../../utils/documents'

/**
 * Fetch and merge employer-wide counters and worker-specific counters.
 * Always returns only counters belonging to the provided worker, but ensures
 * any counters missing from the worker-specific query are added from the
 * employer-wide query.
 *
 * @param workerId - The worker ID
 * @param organizationId - The employer/organization ID
 * @returns React Query-like result with merged counters for the worker
 */
export const useWorkerCounters = (
  workerId: string | undefined,
  organizationId: string,
): UseQueryResult<WorkerCounter[]> => {
  // Fetch worker-specific counters
  const workerQuery = useWorkerCountersQuery(
    { employer: organizationId, worker: workerId },
    { enabled: Boolean(workerId && organizationId) },
  )

  // Fetch employer templates to know which counters should exist
  const templatesQuery = useWorkerCounterTemplatesQuery(
    { employer: organizationId },
    { enabled: Boolean(organizationId) },
  )

  // Mutation to create or get missing counters
  const createOrGetMutation = useCreateOrGetCounterMutation()

  // Side effect: for any template that is missing, trigger a create-or-get
  useEffect(() => {
    const workerCounters = workerQuery.data || []
    const templates = templatesQuery.data || []

    if (!workerId || !organizationId || templates.length === 0) return

    for (const tmpl of templates) {
      const existingCounter = workerCounters.some(c => compareDocsById(c.template, tmpl))
      if (existingCounter) continue

      createOrGetMutation.mutateAsync({
        employer: organizationId,
        worker: workerId,
        template: tmpl._id,
      } as any)
    }
  }, [workerId, organizationId, workerQuery.data, templatesQuery.data])

  // Re-expose the workerQuery result object to keep query state/methods stable
  // but override its data with our merged list.
  return {
    ...workerQuery,
    data: workerQuery.data,
  } as UseQueryResult<WorkerCounter[]>
}
